import { useEffect, useState } from 'react';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import { attachmentIdOf, type DecryptedContent } from '../lib/e2ee';
import { Icon } from './Icon';

/**
 * Only these decrypted types are shown inside the page. Everything else (HTML, SVG, PDF…) is offered as a
 * download with a neutral type: a blob URL has our origin, so active content must never be rendered.
 */
const INLINE: Record<string, 'image' | 'audio' | 'video'> = {
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'image/gif': 'image',
  'audio/webm': 'audio',
  'audio/ogg': 'audio',
  'audio/mp4': 'audio',
  'audio/mpeg': 'audio',
  'video/mp4': 'video',
  'video/webm': 'video',
};
/** Larger files are only fetched and decrypted on request. */
const AUTO_LOAD_BYTES = 10 * 1024 * 1024;

function baseType(mime: string | undefined): string {
  return (mime ?? '').split(';')[0]!.trim().toLowerCase();
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const cache = new Map<string, Promise<string>>();

/** Downloads the ciphertext, decrypts it on this device and returns a blob URL (cached per attachment). */
function decryptToUrl(fetchAndDecrypt: () => Promise<Uint8Array>, id: string, type: string): Promise<string> {
  let p = cache.get(id);
  if (!p) {
    p = fetchAndDecrypt().then((bytes) => URL.createObjectURL(new Blob([bytes as BlobPart], { type })));
    p.catch(() => cache.delete(id));
    cache.set(id, p);
  }
  return p;
}

export function EncryptedFileView({ content }: { content: DecryptedContent }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const chat = useChat();
  const e2ee = chat.e2eeReady ? store?.getE2ee() : null;
  const id = attachmentIdOf(content.file?.url);
  const mime = baseType(content.info?.mimetype);
  const kind = INLINE[mime];
  const size = content.info?.size ?? 0;
  const name = (content.body ?? 'file').slice(0, 255);
  const [requested, setRequested] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const wanted = Boolean(kind) && (size <= AUTO_LOAD_BYTES || requested);

  const load = (asDownload: boolean) => {
    if (!e2ee || !id || !content.file) return Promise.reject(new Error('unavailable'));
    const file = content.file;
    return decryptToUrl(
      async () => {
        const res = await fetch(`/api/v1/attachments/${id}`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(String(res.status));
        return e2ee.decryptFile(new Uint8Array(await res.arrayBuffer()), file);
      },
      `${id}:${asDownload ? 'dl' : 'view'}`,
      asDownload || !kind ? 'application/octet-stream' : mime,
    );
  };

  useEffect(() => {
    if (!wanted) return;
    let alive = true;
    load(false).then(
      (u) => alive && setUrl(u),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load depends only on the values below
  }, [wanted, id, e2ee]);

  const download = () => {
    void load(true).then(
      (u) => {
        const a = document.createElement('a');
        a.href = u;
        a.download = name;
        a.rel = 'noopener';
        a.click();
      },
      () => setFailed(true),
    );
  };

  if (failed) return <p className="bubble-text muted">{t('e2ee.fileFailed')}</p>;
  if (kind && url) {
    if (kind === 'image') {
      return (
        <button type="button" className="attachment-image link-button" onClick={download} title={t('chat.download')}>
          <img src={url} alt={name} />
        </button>
      );
    }
    if (kind === 'audio') return <audio className="attachment-voice" controls src={url} aria-label={name} />;
    return <video className="attachment-video" controls playsInline src={url} aria-label={name} />;
  }
  return (
    <button type="button" className="attachment-file" onClick={kind && !wanted ? () => setRequested(true) : download}>
      <Icon name="file" />
      <span className="truncate">🔒 {name}</span>
      {size > 0 && <span className="hint">{formatSize(size)}</span>}
      <span className="visually-hidden">{t('chat.download')}</span>
    </button>
  );
}
